import { useCallback, useMemo, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router'

import { isChildActive, isItemActive, splitDestination } from './navActive'
import {
  NAVIGATION,
  PLATFORM_NAVIGATION,
  type NavChild,
  type NavItem,
  type NavSection,
} from './navigation'
import { useNavBadges, type NavBadges } from './useNavBadges'
import { WorkspaceSwitcher } from './WorkspaceSwitcher'

import { Brand } from '@/components/primitives/Brand'
import { Icon, NAV_ICONS } from '@/components/primitives/Icon'
import type { InboxCounts } from '@/features/inbox/types'
import { useCan, useCanAny } from '@/lib/permissions/useCan'
import { cn } from '@/lib/utils/cn'

const EXPANDED_KEY = 'convo:sidebar-expanded'

function readExpanded(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(EXPANDED_KEY)
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {}
  } catch {
    return {}
  }
}

/**
 * The workspace navigation.
 *
 * Groups expand in place rather than opening a flyout, because the sub-items
 * are FILTERS the agent switches between all day — "unread", "breached SLA",
 * "invoices" — and a menu that closes on every click makes that miserable.
 *
 * Colour carries meaning here, it is not decoration: every item keeps the same
 * tone as the status it leads to, so "breached SLA" is the same red on the
 * sidebar, in the ticket list, and on the ticket itself. Tones resolve to
 * contrast-verified token pairs in workspace.css, in both themes.
 */
export function Sidebar({
  workspaceSlug,
  collapsed,
  hasCommerce = true,
  scope = 'workspace',
}: {
  workspaceSlug: string
  collapsed: boolean
  hasCommerce?: boolean
  scope?: 'workspace' | 'platform'
}) {
  const platform = scope === 'platform'
  const sections = platform
    ? PLATFORM_NAVIGATION
    : NAVIGATION.filter((s) => !s.requiresCommerce || hasCommerce)

  // Platform paths are absolute; workspace paths hang off the slug.
  const base = platform ? '' : `/w/${workspaceSlug}`

  const badges = useNavBadges()
  const [overrides, setOverrides] = useState(readExpanded)
  const toggle = useCallback((path: string, next: boolean) => {
    setOverrides((prev) => {
      const updated = { ...prev, [path]: next }
      try {
        localStorage.setItem(EXPANDED_KEY, JSON.stringify(updated))
      } catch {
        /* Preference only. */
      }
      return updated
    })
  }, [])

  return (
    <nav
      aria-label={platform ? 'Platform' : 'Main'}
      className={cn(
        'workspace-sidebar flex h-full flex-col border-r border-border bg-surface',
        platform && 'workspace-sidebar--platform',
        collapsed
          ? 'w-[var(--layout-sidebar-collapsed)]'
          : 'w-[var(--layout-sidebar)]',
      )}
    >
      <div className="sidebar-brand">
        <Brand compact={collapsed} />
      </div>
      <div className="p-3">
        {collapsed ? null : platform ? (
          <Link to="/workspaces" className="sidebar-escape">
            <Icon name="arrow" size={15} style={{ transform: 'rotate(180deg)' }} />
            <span>Back to workspaces</span>
          </Link>
        ) : (
          <WorkspaceSwitcher currentSlug={workspaceSlug} />
        )}
      </div>

      <div className="sidebar-scroll flex-1 overflow-y-auto px-3 pb-4">
        {sections.map((section, index) => (
          <Section
            key={section.label ?? index}
            section={section}
            base={base}
            collapsed={collapsed}
            isFirst={index === 0}
            badges={badges}
            overrides={overrides}
            onToggle={toggle}
            enforcePermissions={!platform}
          />
        ))}
      </div>
      {!collapsed && (
        <div className="sidebar-footer">
          <Icon name="spark" size={16} />
          <span>Your conversations. Connected.</span>
        </div>
      )}
    </nav>
  )
}

function Section({
  section,
  base,
  collapsed,
  isFirst,
  badges,
  overrides,
  onToggle,
  enforcePermissions,
}: {
  section: NavSection
  base: string
  collapsed: boolean
  isFirst: boolean
  badges: NavBadges
  overrides: Record<string, boolean>
  onToggle: (path: string, next: boolean) => void
  enforcePermissions: boolean
}) {
  const permitted = useCanAny(
    ...section.items.flatMap((item) => (item.permission ? [item.permission] : [])),
  )
  if (
    enforcePermissions &&
    !permitted &&
    section.items.every((item) => item.permission)
  ) {
    return null
  }

  return (
    <div className={cn('nav-section', !isFirst && 'mt-5')}>
      {section.label && !collapsed && (
        <p
          className={cn(
            'nav-section-label mb-1 px-2 text-eyebrow font-semibold tracking-[0.06em] uppercase',
            section.tone && `nav-tone-${section.tone}`,
          )}
        >
          <span aria-hidden="true" className="nav-section-dot" />
          {section.label}
        </p>
      )}
      {!isFirst && collapsed && <div className="mx-2 my-3 border-t border-border" />}

      <ul>
        {section.items.map((item) => (
          <SidebarItem
            key={item.path}
            item={item}
            base={base}
            collapsed={collapsed}
            badges={badges}
            expandedOverride={overrides[item.path]}
            onToggle={onToggle}
          />
        ))}
      </ul>
    </div>
  )
}

function SidebarItem({
  item,
  base,
  collapsed,
  badges,
  expandedOverride,
  onToggle,
}: {
  item: NavItem
  base: string
  collapsed: boolean
  badges: NavBadges
  expandedOverride: boolean | undefined
  onToggle: (path: string, next: boolean) => void
}) {
  const { pathname, search } = useLocation()
  const params = useMemo(() => new URLSearchParams(search), [search])

  // A hidden item is a courtesy; the route guard and the API enforce it.
  const allowed = useCan(...(item.permission ? [item.permission] : []))
  const canQuickAction = useCan(
    ...(item.quickAction?.permission ? [item.quickAction.permission] : []),
  )

  const active = isItemActive(item, base, pathname)
  const children = item.children ?? []
  const quickAction = item.quickAction && canQuickAction ? item.quickAction : undefined
  const expandable = (children.length > 0 || Boolean(quickAction)) && !collapsed

  // Open when the route is inside the group, unless the user has said
  // otherwise — an explicit choice outlives navigating away.
  const expanded = expandedOverride ?? active
  const count = item.badge ? badges[item.badge] : undefined
  const groupId = `nav-group-${item.path.replace(/\W+/g, '-') || 'dashboard'}`

  if (item.permission && !allowed) return null

  return (
    <li className={cn('nav-entry', item.tone && `nav-tone-${item.tone}`)}>
      <div className="nav-row">
        <NavLink
          to={`${base}${item.path}`}
          end={item.path === ''}
          title={collapsed ? item.label : undefined}
          className={cn(
            'nav-item flex min-h-[44px] flex-1 items-center gap-3 rounded-control px-3 text-compact',
            'transition-colors duration-control ease-standard',
            active ? 'nav-item--active font-medium' : 'text-text-secondary',
            collapsed && 'justify-center px-0',
          )}
        >
          <span aria-hidden="true" className="nav-icon-chip">
            <Icon name={NAV_ICONS[item.path] ?? 'dashboard'} size={17} />
            {/* Collapsed to a rail there is no room for the number, but
                "something is waiting here" still has to survive. */}
            {collapsed && count !== undefined && count > 0 && (
              <span className="nav-chip-dot" />
            )}
          </span>
          {!collapsed && <span className="nav-label truncate">{item.label}</span>}
          {count !== undefined && count > 0 && (
            <span className={cn('nav-badge', collapsed && 'sr-only')}>
              {count > 99 ? '99+' : count}
            </span>
          )}
        </NavLink>

        {expandable && (
          <button
            type="button"
            className="nav-disclosure"
            aria-expanded={expanded}
            aria-controls={groupId}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${item.label}`}
            onClick={() => onToggle(item.path, !expanded)}
          >
            <Icon
              name="chevron"
              size={14}
              style={{ transform: expanded ? 'rotate(90deg)' : 'none' }}
            />
          </button>
        )}
      </div>

      {expandable && (
        <ul id={groupId} className="nav-children" hidden={!expanded}>
          {children.map((child) => (
            <SidebarChild
              key={child.to}
              child={child}
              siblings={children}
              base={base}
              pathname={pathname}
              params={params}
              counts={badges.inbox}
            />
          ))}
          {quickAction && (
            <li>
              <Link
                to={`${base}${item.path}?${quickAction.flag}=1`}
                className="nav-quick-action"
              >
                <Icon name="plus" size={15} />
                <span>{quickAction.label}</span>
              </Link>
            </li>
          )}
        </ul>
      )}
    </li>
  )
}

/** Counts the inbox endpoint already returns, matched to the sub-item. */
function childCount(to: string, counts: InboxCounts | undefined): number | undefined {
  if (!counts) return undefined
  const { path, params } = splitDestination(to)
  if (path !== '/inbox') return undefined
  const view = params.get('view') ?? 'all'
  return view in counts ? counts[view as keyof InboxCounts] : undefined
}

function SidebarChild({
  child,
  siblings,
  base,
  pathname,
  params,
  counts,
}: {
  child: NavChild
  siblings: readonly NavChild[]
  base: string
  pathname: string
  params: URLSearchParams
  counts: InboxCounts | undefined
}) {
  const allowed = useCan(...(child.permission ? [child.permission] : []))
  if (child.permission && !allowed) return null

  const active = isChildActive(child, siblings, base, pathname, params)
  const count = childCount(child.to, counts)

  return (
    <li>
      <Link
        to={`${base}${child.to}`}
        aria-current={active ? 'page' : undefined}
        className={cn(
          'nav-child',
          child.tone && `nav-tone-${child.tone}`,
          active && 'nav-child--active',
        )}
      >
        <span aria-hidden="true" className="nav-child-dot" />
        <span className="truncate">{child.label}</span>
        {count !== undefined && count > 0 && (
          <span className="nav-child-count">{count > 99 ? '99+' : count}</span>
        )}
      </Link>
    </li>
  )
}
